import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import './rcm.css';

// The one-page teaser as a camera. The sheet is the spine of the tour, so
// `frame` (from ?fig=teaser&frame=N) is a BAND of the sheet, not a sheet:
// the viewport reshapes to the band and the sheet glides and scales into it
// (~650 ms), with everything outside the band dimmed and a chip naming it.
// `view` (?view=unnamed) picks the unnamed sibling; default is the sheet
// prepared for the sponsor. Clicking the whole sheet zooms to the band
// under the cursor; clicking a band returns to the whole.
//
// REGIONS are fractions of the US Letter page, measured from the built
// PDF's text blocks (python: fitz page.get_text('blocks')) — re-measure if
// the teaser's layout moves (scripts/build-teaser.mjs rcm-revelstoke).
const SHEETS = [
  { id: 'sponsor', label: 'Prepared for the sponsor', png: '/concepts/revenue-cycle-teaser.png', pdf: '/concepts/revenue-cycle-teaser.pdf', note: 'Modeled on the urgent care platform’s public shape; no portfolio company named; "Prepared for Revelstoke Capital Partners" in the kicker.' },
  { id: 'unnamed', label: 'Unnamed · any healthcare sponsor', png: '/concepts/revenue-cycle-teaser-unnamed.png', pdf: '/concepts/revenue-cycle-teaser-unnamed.pdf', note: 'The same page at a generic $500M outpatient scale — reusable for other sponsors.' },
];
export const REGIONS = [
  { id: 'whole', label: 'The sheet — one US Letter page, read top to bottom', box: [0, 0, 1, 1] },
  { id: 'header', label: 'Map the revenue cycle, then optimize it', box: [0.035, 0.012, 0.965, 0.172] },
  { id: 'journey', label: 'The claim’s journey — amber is where the money leaks', box: [0.035, 0.153, 0.965, 0.214] },
  { id: 'loop', label: 'Part I · Map the cycle → optimize → build → prove', box: [0.035, 0.203, 0.965, 0.372] },
  { id: 'baseline', label: 'Part II · The baseline you already have', box: [0.035, 0.37, 0.335, 0.632] },
  { id: 'initiatives', label: 'Part III · Five initiatives the map proposes', box: [0.33, 0.37, 0.625, 0.678] },
  { id: 'ledger', label: 'Part V · Prove it, claim by claim', box: [0.62, 0.37, 0.965, 0.684] },
  { id: 'plan', label: 'Part IV · The 90 days, then the portfolio', box: [0.035, 0.68, 0.965, 0.842] },
  { id: 'deliverable', label: 'NoVA runs the path — and two things it never does', box: [0.035, 0.838, 0.965, 0.988] },
];
const PAGE_RATIO = 792 / 612; // US Letter, height / width
const clampFrame = (n) => Math.min(Math.max(0, n), REGIONS.length - 1);

export default function TeaserSheet({ frame = null, view }) {
  const viewIdx = view === 'unnamed' ? 1 : 0;
  const [sheet, setSheet] = useState(viewIdx);
  useEffect(() => { setSheet(viewIdx); }, [viewIdx]);
  const [i, setI] = useState(() => (frame != null ? clampFrame(frame) : 0));
  useEffect(() => { if (frame != null) setI(clampFrame(frame)); }, [frame]);

  // The viewport's width drives every size; measure it, re-measure on resize.
  const camRef = useRef(null);
  const [vw, setVw] = useState(0);
  const [winH, setWinH] = useState(() => (typeof window !== 'undefined' ? window.innerHeight : 800));
  useLayoutEffect(() => {
    const el = camRef.current;
    if (!el) return undefined;
    const measure = () => { setVw(el.clientWidth); setWinH(window.innerHeight); };
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (ro) ro.observe(el); else window.addEventListener('resize', measure);
    return () => { if (ro) ro.disconnect(); else window.removeEventListener('resize', measure); };
  }, []);

  const s = SHEETS[sheet];
  const r = REGIONS[i];
  const whole = i === 0;
  const baseH = vw * PAGE_RATIO;
  const [x0, y0, x1, y1] = r.box;
  const rw = (x1 - x0) * vw;
  const rh = (y1 - y0) * baseH;
  // The whole sheet keeps its natural height (the column scrolls it); a band
  // reshapes the viewport to its own aspect, within the window.
  const vh = whole ? baseH : Math.min(Math.max(140, (vw * rh) / Math.max(1, rw)), winH * 0.8);
  const scale = whole ? 1 : Math.min(vw / Math.max(1, rw), vh / Math.max(1, rh)) * 0.97;
  const tx = whole ? 0 : vw / 2 - scale * (x0 * vw + rw / 2);
  const ty = whole ? 0 : vh / 2 - scale * (y0 * baseH + rh / 2);

  const onSheetClick = useCallback((e) => {
    if (!whole) { setI(0); return; }
    const el = e.currentTarget.getBoundingClientRect();
    const fx = (e.clientX - el.left) / el.width;
    const fy = (e.clientY - el.top) / el.height;
    const hit = REGIONS.slice(1)
      .filter(({ box: [a, b, c, d] }) => fx >= a && fx <= c && fy >= b && fy <= d)
      .sort((p, q) => (p.box[2] - p.box[0]) * (p.box[3] - p.box[1]) - (q.box[2] - q.box[0]) * (q.box[3] - q.box[1]))[0];
    if (hit) setI(REGIONS.indexOf(hit));
  }, [whole]);

  return (
    <div className="as-screen rcm-teaser" data-nova-note="The one-page revenue-cycle teaser as a camera. Frames are bands of the sheet in reading order: 0 whole, 1 header, 2 claim's journey, 3 the loop, 4 baseline column, 5 initiatives column, 6 ledger column, 7 the 90-day plan, 8 NoVA and the deliverable. ?view=unnamed shows the unnamed sheet. The PDF link is what gets sent.">
      <div className="as-tabs" role="tablist">
        {SHEETS.map((t, k) => (
          <button key={t.id} type="button" role="tab" aria-selected={k === sheet} className={k === sheet ? 'on' : ''} onClick={() => setSheet(k)}>{t.label}</button>
        ))}
        <span className="as-tabctl" role="group" aria-label="band controls">
          <a className="cs-btn cs-btn-sm cs-btn-outline" href={s.pdf} target="_blank" rel="noreferrer">Open the PDF</a>
          <button type="button" onClick={() => setI((i + REGIONS.length - 1) % REGIONS.length)} aria-label="previous band">‹</button>
          <button type="button" onClick={() => setI((i + 1) % REGIONS.length)} aria-label="next band">›</button>
          <small>{i + 1} / {REGIONS.length}</small>
        </span>
      </div>
      <div ref={camRef} className={`rcm-cam ${whole ? 'is-whole' : 'is-band'}`} style={{ height: vw ? vh : undefined }}>
        <div
          className="rcm-cam-sheet"
          style={{ width: vw || '100%', height: vw ? baseH : undefined, transform: `translate(${tx}px, ${ty}px) scale(${scale})` }}
          onClick={onSheetClick}
          role="button"
          tabIndex={0}
          aria-label={whole ? 'Zoom to a band of the teaser' : `Back to the whole sheet (showing ${r.label})`}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setI(whole ? 1 : 0); } }}
        >
          <img src={s.png} alt={`The revenue-cycle teaser — ${s.label}`} loading="eager" draggable="false" />
          {!whole && (
            <span className="rcm-cam-dim" aria-hidden="true" style={{ left: `${x0 * 100}%`, top: `${y0 * 100}%`, width: `${(x1 - x0) * 100}%`, height: `${(y1 - y0) * 100}%` }} />
          )}
        </div>
      </div>
      <div className="as-foot">
        <span className={`as-foot-chip rcm-cam-chip ${whole ? '' : 'is-on'}`} aria-live="polite">{whole ? 'US Letter · one page' : r.label}</span>
        <span>{s.note}{whole ? ' Click a part of the sheet to zoom to it.' : ' Click the sheet to see the whole page.'}</span>
      </div>
    </div>
  );
}
